import axios from 'axios';
import { API_BASE_URL, API_ENDPOINTS } from '../config/api';

// Configure axios timeout for API requests (individual requests should be fast)
const axiosConfig = {
  timeout: 30000, // 30 seconds for individual HTTP requests (polling calls should be quick)
  headers: {
    'Content-Type': 'application/json'
  }
};

// Create axios instance with timeout
const apiClient = axios.create(axiosConfig);

// Add request interceptor to include auth token (same as App.js global interceptor)
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    console.log(`🤖 Agentic request to: ${config.method?.toUpperCase()} ${config.url}`);
    console.log(`🔑 Agentic token in localStorage: ${token ? 'Present' : 'Missing'}`);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log(`✅ Agentic Authorization header set`);
    } else {
      console.log(`❌ No token found for agentic request, will be unauthenticated`);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle 401s (same as App.js global interceptor)
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      console.log(`🚫 Agentic 401 Unauthorized - removing token and reloading`);
      localStorage.removeItem('authToken');
      window.location.reload();
    }
    return Promise.reject(error);
  }
);

/**
 * AgenticService - Handles all interactions with the AI Assistant API
 * 
 * Key features:
 * - Send chat messages to OpenAI Assistant
 * - Poll for async responses
 * - Retrieve conversation history
 * - Handle errors and loading states
 * - Manage JWT authentication automatically (via axios interceptors)
 */

class AgenticService {
  constructor() {
    this.activePolls = new Map(); // Track active polling for requests
  }

  /**
   * Start a new chat with the AI Assistant
   * 
   * @param {string} message - User message
   * @param {string} threadId - Optional thread ID for conversation continuity
   * @returns {Promise<Object>} Response with requestId and status
   */
  async startChat(message, threadId = null) {
    try {
      console.log('🤖 Starting chat with message:', message);
      
      const response = await apiClient.post(`${API_BASE_URL}${API_ENDPOINTS.AGENTIC.CHAT}`, {
        message: message.trim(),
        threadId: threadId
      });

      console.log('✅ Chat started successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error starting chat:', error);
      
      if (error.response?.status === 401) {
        throw new Error('Authentication required. Please log in.');
      }
      
      if (error.response?.status === 400) {
        throw new Error(error.response.data?.error || 'Invalid message format');
      }
      
      throw new Error(error.response?.data?.error || 'Failed to start chat');
    }
  }

  /**
   * Poll for response to an async chat request
   * 
   * @param {string} requestId - Request ID from startChat
   * @param {function} onUpdate - Callback for status updates
   * @param {number} maxAttempts - Maximum polling attempts (default: 150 = 5 minutes)
   * @param {number} intervalMs - Polling interval in milliseconds (default: 2000)
   * @returns {Promise<Object>} Final response when completed
   */
  async pollForResponse(requestId, onUpdate = null, maxAttempts = 150, intervalMs = 2000) {
    const timeoutMinutes = Math.round((maxAttempts * intervalMs) / 60000);
    console.log(`🔄 Starting to poll for response: ${requestId} (timeout: ${timeoutMinutes} minutes, ${maxAttempts} attempts every ${intervalMs}ms)`);
    
    // Cancel any existing poll for this request
    if (this.activePolls.has(requestId)) {
      clearInterval(this.activePolls.get(requestId));
    }

    return new Promise((resolve, reject) => {
      let attempts = 0;
      
      const poll = async () => {
        attempts++;
        
        try {
          const response = await this.getResponse(requestId);
          
          if (onUpdate) {
            onUpdate({
              status: response.status,
              statusDetails: response.statusDetails,
              attempt: attempts,
              maxAttempts: maxAttempts
            });
          }

          // Check if we have a final response
          if (response.status === 'completed') {
            console.log('✅ Chat completed successfully');
            this.activePolls.delete(requestId);
            resolve(response);
            return;
          }
          
          if (response.status === 'error') {
            console.error('❌ Chat failed with error:', response.error);
            this.activePolls.delete(requestId);
            reject(new Error(response.error || 'Chat processing failed'));
            return;
          }

          // Continue polling if still processing or pending
          if (attempts >= maxAttempts) {
            const timeoutMinutes = Math.round((maxAttempts * intervalMs) / 60000);
            console.error(`❌ Polling timeout after ${maxAttempts} attempts (${timeoutMinutes} minutes)`);
            this.activePolls.delete(requestId);
            reject(new Error(`Request timeout after ${timeoutMinutes} minutes - the AI request is taking longer than expected. Please try again.`));
            return;
          }

          // Schedule next poll
          setTimeout(poll, intervalMs);
          
        } catch (error) {
          console.error('❌ Error during polling:', error);
          this.activePolls.delete(requestId);
          reject(error);
        }
      };

      // Start polling
      poll();
    });
  }

  /**
   * Get response for a specific request ID
   * 
   * @param {string} requestId - Request ID to check
   * @returns {Promise<Object>} Current status and response
   */
  async getResponse(requestId) {
    try {
      const response = await apiClient.get(`${API_BASE_URL}${API_ENDPOINTS.AGENTIC.RESPONSE(requestId)}`);
      return response.data;
    } catch (error) {
      console.error('❌ Error getting response:', error);
      
      if (error.response?.status === 404) {
        throw new Error('Request not found');
      }
      
      throw new Error(error.response?.data?.error || 'Failed to get response');
    }
  }

  /**
   * Get list of conversation threads for the current user
   * 
   * @param {number} limit - Maximum number of threads (default: 10)
   * @returns {Promise<Object>} Threads list
   */
  async getUserThreads(limit = 10) {
    try {
      console.log('📝 Getting user conversation threads');
      
      const response = await apiClient.get(`${API_BASE_URL}${API_ENDPOINTS.AGENTIC.THREADS}`, {
        params: { limit }
      });

      console.log('✅ Retrieved user threads:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error getting user threads:', error);
      
      if (error.response?.status === 401) {
        throw new Error('Authentication required. Please log in.');
      }
      
      throw new Error(error.response?.data?.error || 'Failed to get conversation threads');
    }
  }

  /**
   * Get conversation history for a thread
   * 
   * @param {string} threadId - Thread ID
   * @param {number} limit - Maximum number of messages (default: 50)
   * @returns {Promise<Object>} Messages and thread info
   */
  async getThreadMessages(threadId, limit = 50) {
    try {
      console.log(`📜 Getting thread messages for: ${threadId}`);
      
      const response = await apiClient.get(`${API_BASE_URL}${API_ENDPOINTS.AGENTIC.THREAD_MESSAGES(threadId)}`, {
        params: { limit }
      });

      console.log('✅ Retrieved thread messages:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error getting thread messages:', error);
      
      if (error.response?.status === 404) {
        throw new Error('Thread not found');
      }
      
      throw new Error(error.response?.data?.error || 'Failed to get thread messages');
    }
  }

  /**
   * Cancel active polling for a request
   * 
   * @param {string} requestId - Request ID to cancel
   */
  cancelPolling(requestId) {
    if (this.activePolls.has(requestId)) {
      clearInterval(this.activePolls.get(requestId));
      this.activePolls.delete(requestId);
      console.log(`🛑 Cancelled polling for: ${requestId}`);
    }
  }

  /**
   * Cancel all active polling
   */
  cancelAllPolling() {
    for (const [requestId, intervalId] of this.activePolls) {
      clearInterval(intervalId);
      console.log(`🛑 Cancelled polling for: ${requestId}`);
    }
    this.activePolls.clear();
  }

  /**
   * Check if the agentic service is healthy
   * 
   * @returns {Promise<Object>} Health status
   */
  async checkHealth() {
    try {
      const response = await apiClient.get(`${API_BASE_URL}${API_ENDPOINTS.AGENTIC.HEALTH}`);
      return response.data;
    } catch (error) {
      console.error('❌ Health check failed:', error);
      throw new Error('Agentic service is not available');
    }
  }

  /**
   * Format message for display
   * 
   * @param {Object} message - Message object from API
   * @returns {Object} Formatted message
   */
  formatMessage(message) {
    return {
      id: message.id || Date.now(),
      content: message.content || message.message || '',
      role: message.role || 'user',
      timestamp: message.timestamp || message.createdAt || new Date().toISOString(),
      threadId: message.threadId || null
    };
  }

  /**
   * Parse function calls from AI response
   * 
   * @param {string} response - AI response content
   * @returns {Array} Array of function calls if any
   */
  parseFunctionCalls(response) {
    try {
      // Look for function calls in the response
      const functionCallRegex = /\[Function: (\w+)\]/g;
      const calls = [];
      let match;
      
      while ((match = functionCallRegex.exec(response)) !== null) {
        calls.push({
          function: match[1],
          raw: match[0]
        });
      }
      
      return calls;
    } catch (error) {
      console.error('Error parsing function calls:', error);
      return [];
    }
  }
}

// Create singleton instance
const agenticService = new AgenticService();

export default agenticService; 